import { Selection, EnterElement } from "d3";
import { D3OneToOneRenderable } from "../../../D3/D3OneToOneRenderable";
import { ModalityGraphGroupReactCallbacks } from "../../../../Types/ReactCallbacks";
import { D3LegendLabels } from "./D3LegendLabels";
import { DeltaRegion, LineTraceConfig, SolidColorTraceDataConfig, TraceDataConfig } from "../../../../Types/Trace";
import { MobergFontFamily } from "../../../../../../../../Moberg";
import { MODALITY_LABEL_FONT_SIZE } from "../../Constants";
import { HarmonizedMedication } from "../../../../../../../../Managers/VisualizationManager/ToolBar/Modals/MedicationGraphModalities/Medications";
import { ModalityPageManager } from "../../../../Data/ModalityPageManager";

export type D3ModalityGraphLegendConfig = {
    traces: TraceDataConfig[]
    width: number
    graphId: string
    patientModalities: string[]
    patientMedications: HarmonizedMedication[]
    isClickDisabled?: boolean
}

export class D3ModalityGraphLegend extends D3OneToOneRenderable<SVGGElement, SVGGElement, D3ModalityGraphLegendConfig, ModalityGraphGroupReactCallbacks> {
    private height = 120
    private legendLabels?: D3LegendLabels
    private pageManager: ModalityPageManager

    constructor(root: SVGGElement, config: D3ModalityGraphLegendConfig, pageManager: ModalityPageManager, reactCallbacks: ModalityGraphGroupReactCallbacks) {
        super(root, config, "d3-modality-graph-legend", reactCallbacks)
        this.pageManager = pageManager
        this.render()
    }

    protected enter(newElements: Selection<EnterElement, D3ModalityGraphLegendConfig, any, any>): Selection<SVGGElement, D3ModalityGraphLegendConfig, any, any> {
        const legendContainer = newElements
            .append("g")
            .attr("class", this.className)
            .attr("transform", `translate(1, ${this.height})`)
            .style("user-select", "none") // disables selecting the text by dragging
            
        const foreignObject = legendContainer.append("foreignObject")
            .attr("width", this.config.width)
            .attr("height", this.height)
            .attr("transform", `translate(0, -${this.height})`)
            .style("font-weight", "bold")
            .style("font-family", MobergFontFamily.REGULAR)
            .style("font-size", MODALITY_LABEL_FONT_SIZE)

        foreignObject.append<HTMLDivElement>("xhtml:div")
            .style("display", "flex")
            .style("width", "fit-content")
            .style("background-color", "var(--background-color)")
            .style("padding", "0 4px")
            .style("gap", "15px")
            .style("flex-wrap", "wrap")
            .each((config, index, nodes) => this.createChildren(config, index, nodes))
               
        return legendContainer
    }

    update = (updatedLegends: Selection<any, any, any, any>) => {
        // The xhtml div loses its styling
        updatedLegends
            .select("foreignObject")
            .attr("width", this.config.width)
                .select("div")
                .style("padding", "0 4px")
                .style("width", this.config.width)
                .style("flex-wrap", "wrap")

        this.legendLabels?.render()
        return updatedLegends
    }

    protected createChildren(config: D3ModalityGraphLegendConfig, index: number, nodes: ArrayLike<HTMLDivElement>): void {
        const root = nodes[index]
        this.legendLabels = new D3LegendLabels(root, { 
            traces: this.config.traces.map(this.traceWithSolidColor), 
            graphId: this.config.graphId, 
            isClickDisabled: this.config.isClickDisabled,
            patientModalities: this.config.patientModalities,
            patientMedications: this.config.patientMedications,
        }, this.pageManager, this.reactCallbacks)
    }

    protected updateDerivedState(): void {
        this.legendLabels?.updateConfig({ 
            traces: this.config.traces.map(this.traceWithSolidColor), 
            graphId: this.config.graphId,
            isClickDisabled: this.config.isClickDisabled,
            patientModalities: this.config.patientModalities,
            patientMedications: this.config.patientMedications,
        })
    }

    private traceWithSolidColor(trace: TraceDataConfig): SolidColorTraceDataConfig {
        // This adapter makes sure that each trace has a solid CSS-compatible color property.
        const fallback = getComputedStyle(document.documentElement).getPropertyValue("--color-chart-trace-fallback").trim() || "#000"
        let solidColor = fallback
        const color = (trace as LineTraceConfig | DeltaRegion).color

        if (color === undefined) {
            solidColor = fallback
        } else if (typeof color === "string") {
            solidColor = color
        } else {
            solidColor = `rgb(${color.r}, ${color.g}, ${color.b})`
        }

        return {...trace, color: solidColor}
    }
}